import React from "react";
import {Button, Form, message, Row, Table, Tooltip} from "antd";
import {CheckOutlined, DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {ColumnsType} from "antd/es/table";
import IFormElementFunctions from "../../../../../model/interface/form/IFormElementFunctions";
import IEntityTableOptions from "../../../../../model/interface/form/elementOptions/IEntityTableOptions";
import FormElementField from "./FormElementField";
import IFieldOptions from "../../../../../model/interface/form/elementOptions/IFieldOptions";
import IField from "../../../../../model/interface/dataStorage/IField";
import IContentType from "../../../../../model/interface/dataStorage/IContentType";
import {connect} from "react-redux";
import selectors from "../../../../../redux/selectors";
import _ from "underscore";
import LocaleText from "../../../settings/dictionary/LocaleText";
import {IAppState} from "../../../../../redux/store";
import Utils from "../../../../../utils";
import IWidgetProps from "../../../../../model/interface/widget/IWidgetProps";
import FormFieldType from "./formField/FormFieldType";

interface IProps extends IWidgetProps<IFormElementFunctions, IEntityTableOptions> {
    className: string,
    findContentTypeByClass: (className: string) => IContentType
    getText: (code: string, fallBack?: string) => string
}

class FormElementEntityTable extends React.Component<IProps> {

    getContentType() {
        const currentField = this.props.functions.getNode(this.props.id).field;
        const field = typeof currentField === 'object'
            ? currentField
            : _.findWhere(this.props.functions.getContentType().fields, {uuid: currentField});
        return this.props.findContentTypeByClass(field!.targetEntity!);
    }

    getFormListName() {
        const {id, functions} = this.props;
        const field = functions.getNode(id).field as IField;
        return field.name;
    }

    buildColumns(fields: any[]): ColumnsType<any> {
        const {id, functions} = this.props;
        const children = functions.getSortedChildren(id);
        const contentType = this.getContentType();
        const form = functions.getFormRef(); // 👈 Access the form ref instance

        return children.map((child, childIdx) => {
            const options = child.options as IFieldOptions;
            return {
                title: <LocaleText code={options.label || child.field?.label || child.field?.name || ''}/>,
                render: (_text, record) => {
                    const field = _.findWhere(contentType.fields, {uuid: child.fieldUuid});
                    child.field = field;

                    // Safely find the item in the fresh fields array reference
                    const listField = fields.find(f => f?.key === record.key);
                    if (!child.field || !listField) return null;

                    // 🌟 FIX THE MOMENT CRASH NATIVELY USING setFieldValue 🌟
                    const listName = this.getFormListName();
                    const fieldPath = [listName, listField.name, child.field.name];
                    const rawValue = form.current?.getFieldValue(fieldPath);

                    // Run the shifting value through your formatting utility
                    const formattedValue = FormFieldType.formatToForm(options.type, rawValue);

                    if (rawValue !== formattedValue) {
                        form.current?.setFields([{
                            name: fieldPath,
                            value: formattedValue,
                        }]);

                    }
                    return (
                        <>
                            {childIdx === 0 && (
                                <>
                                    <Form.Item
                                        name={[listField.name, '_force_submit']}
                                        initialValue={true}
                                        valuePropName="checked"
                                        hidden
                                    />
                                    <Form.Item
                                        name={[listField.name, '_class']}
                                        hidden
                                    />
                                    <Form.Item
                                        name={[listField.name, 'uuid']}
                                        hidden
                                    />
                                </>
                            )}
                            <FormElementField
                                {...this.props}
                                functions={{
                                    ...this.props.functions,
                                    getContentType: () => contentType,
                                    getNode: (nodeId: string) => ({...this.props.functions.getNode(nodeId), field})
                                }}
                                {...child}
                                field={field}
                                /* 🌟 FIXED: Using strictly listField.key avoids reading dynamic form variables.
                                   This remains completely unchanged during index shifts, preventing row resets! */
                                key={`${child.id}_${listField.key}`}
                                id={`${child.id}_${listField.key}`}
                                options={options}
                                noLabel={true}
                                preview={true}
                                className={'mb-0'}
                                customFieldName={[listField.name, child.field.name]}
                            />
                        </>
                    );
                }
            };
        });
    }

    handleValidate = (index: number) => {
        const {functions} = this.props;
        const form = functions.getFormRef();

        form.current
            ?.validateFields([[this.getFormListName(), index]])
            .then(() => message.success('Valid!'))
            .catch(() => message.error('Invalid!'));
    }

    getActions(options: IEntityTableOptions, add: (value?: any, index?: number) => void, remove: (index: number) => void, fields: any[]): ColumnsType<any> {
        const {className} = this.props;
        return [{
            title: options.add && (
                <Row justify={"end"}>
                    <Button
                        size={"small"}
                        type={'link'}
                        icon={<PlusOutlined/>}
                        onClick={() => add({
                            uuid: Utils.uuid(),
                            _class: className,
                            _force_submit: true
                        })}
                    >
                        Vytvořit nový
                    </Button>
                </Row>
            ),
            render: (_, record) => {
                const currentIndex = fields.findIndex(f => f?.key === record.key);
                if (currentIndex === -1) return null;

                return (
                    <Row key={record.key} justify={"end"} gutter={[10, 10]}>
                        {options.delete && (
                            <Tooltip title={'Smazat'}>
                                <Button
                                    size={"small"}
                                    type={'link'}
                                    danger
                                    icon={<DeleteOutlined/>}
                                    onClick={() => remove(currentIndex)}
                                />
                            </Tooltip>
                        )}
                        {options.validate && (
                            <Tooltip title={'Validate'}>
                                <Button
                                    size={"small"}
                                    type={'link'}
                                    icon={<CheckOutlined/>}
                                    onClick={() => this.handleValidate(currentIndex)}
                                />
                            </Tooltip>
                        )}
                    </Row>
                );
            }
        }];
    }

    render() {
        const {id, functions, options, getText} = this.props;
        const children = functions.getSortedChildren(id);

        if (children.length === 0) return null;

        return (
            <Form.List
                name={this.getFormListName()}
                rules={options.required ? [
                    {
                        validator: async (_, value) => {
                            if (!value || value.length === 0) {
                                return Promise.reject(new Error(getText('form.item.required')));
                            }
                            return Promise.resolve();
                        },
                    }
                ] : []}
            >
                {(fields, {add, remove}, {errors}) => {
                    const reversedFields = [...fields].reverse();

                    return (
                        <div>
                            <Table
                                rowKey="key"
                                pagination={false}
                                size={'small'}
                                dataSource={reversedFields}
                                columns={[
                                    ...this.buildColumns(fields),
                                    ...this.getActions(options, add, remove, fields)
                                ]}
                            />
                            {errors.length > 0 && (
                                <div className="ant-form-item-explain ant-form-item-explain-error"
                                     style={{marginTop: 8, color: '#ff4d4f'}}>
                                    {errors.map((error, idx) => (
                                        <div key={idx}>{error}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }}
            </Form.List>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    return {
        findContentTypeByClass: (className: string) => selectors.contentTypes.findOneBy(state, 'fullClassName', className),
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    };
};

export default connect(mapStateToProps)(FormElementEntityTable);